/**
 * Job-related business logic utilities
 */

import { getTodayInPT } from "../datetime";

/**
 * Associates scopes with their respective jobs.
 *
 * @param {Array} jobs - The list of jobs.
 * @param {Array} scopes - The list of scopes.
 * @param {string|Date} [date] - Optional date to filter scopes. Only scopes matching this date will be included.
 * @returns {Array} An array of tuples where each tuple contains a job and an array of associated scopes.
 *
 * @example
 * // Get all scopes for all jobs
 * const jobsWithScopes = scopesByJob(jobs, scopes);
 *
 * // Get only scopes for a specific date
 * const jobsWithScopesForDate = scopesByJob(jobs, scopes, '2025-07-25');
 * jobsWithScopesForDate.forEach(([job, jobScopes]) => {
 *   console.log(`Job ${job.id} has ${jobScopes.length} scopes for the specified date`);
 * });
 */
export function scopesByJob(jobs, scopes, date = null) {
  if (!jobs || !scopes) {
    return [];
  }

  // Filter scopes by date if date parameter is provided
  const filteredScopes = date
    ? scopes.filter(
        (scope) =>
          scope.date === date ||
          (scope.date instanceof Date &&
            date instanceof Date &&
            scope.date.getTime() === date.getTime()) ||
          (typeof scope.date === "string" &&
            typeof date === "string" &&
            scope.date === date) ||
          (scope.date instanceof Date &&
            typeof date === "string" &&
            scope.date.toISOString().split("T")[0] === date) ||
          (typeof scope.date === "string" &&
            date instanceof Date &&
            scope.date === date.toISOString().split("T")[0])
      )
    : scopes;

  const scopesPerJob = Map.groupBy(filteredScopes, (scope) => scope.job.id);
  // Use job.id to associate scopes and jobs
  return jobs.map((job) => [job, scopesPerJob.get(job.id) ?? []]);
}

/**
 * Gets all workers at a given job, on a given date, from the list of worker_assignments
 * in the scopes.
 *
 * @param {Array} jobs - The list of jobs.
 * @param {Array} scopes - The list of scopes.
 * @param {string|Date} [date] - Optional date to filter scopes. Only scopes matching this date will be included.
 *
 * @returns {Array} An array of worker objects associated with the job on the specified date.
 */
export function getWorkersByJob(jobs, scopes, date = null) {
  if (!jobs || !scopes) {
    return [];
  }

  // Filter scopes by date if date parameter is provided
  const filteredScopes = date
    ? scopes.filter(
        (scope) =>
          scope.date === date ||
          (scope.date instanceof Date &&
            date instanceof Date &&
            scope.date.getTime() === date.getTime()) ||
          (typeof scope.date === "string" &&
            typeof date === "string" &&
            scope.date === date) ||
          (scope.date instanceof Date &&
            typeof date === "string" &&
            scope.date.toISOString().split("T")[0] === date) ||
          (typeof scope.date === "string" &&
            date instanceof Date &&
            scope.date === date.toISOString().split("T")[0])
      )
    : scopes;

  const workersPerJob = Map.groupBy(filteredScopes, (scope) => scope.job.id);

  return jobs.map((job) => {
    const jobScopes = workersPerJob.get(job.id) || [];
    const workerAssignments = jobScopes.flatMap(
      (scope) => scope.worker_assignment || []
    );
    const uniqueWorkers = workerAssignments.filter(
      (worker, index, self) =>
        index === self.findIndex((w) => w.id === worker.id)
    );
    return { ...job, workers: uniqueWorkers };
  });
}

/**
 * Filters jobs by their end date, keeping only jobs that are active or ended within a specified date range.
 *
 * @param {Array} jobs - The list of jobs to filter.
 * @param {Object} [options] - Optional configuration for date filtering.
 * @param {Date|string|null} [options.startDate] - The start date of the range. Defaults to 1 year ago from today.
 * @param {Date|string|null} [options.endDate] - The end date of the range. Defaults to today.
 * @returns {Array} Filtered array of jobs that have no date_end or have date_end within the specified range.
 *
 * @example
 * // Filter jobs to show only active jobs or jobs that ended within the last year (default)
 * const activeJobs = filterJobsByDateRange(allJobs);
 *
 * // Filter jobs with custom date range
 * const recentJobs = filterJobsByDateRange(allJobs, {
 *   startDate: '2024-01-01',
 *   endDate: '2024-12-31'
 * });
 *
 * // Filter jobs that ended in the last 6 months
 * const sixMonthsAgo = new Date();
 * sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
 * const recentEndedJobs = filterJobsByDateRange(allJobs, {
 *   startDate: sixMonthsAgo
 * });
 */
export function filterJobsByDateRange(jobs, options = {}) {
  if (!jobs) return [];

  // Get default dates using centralized datetime utility
  const today = new Date(getTodayInPT());
  const defaultStartDate = new Date(today);
  defaultStartDate.setFullYear(defaultStartDate.getFullYear() - 1);

  // Parse options
  const { startDate = defaultStartDate } = options;

  // Convert dates to Date objects if needed
  const rangeStart =
    startDate instanceof Date ? startDate : new Date(startDate);

  // Filter jobs
  return jobs.filter((job) => {
    // If no date_end, job is active - always include
    if (!job.date_end) return true;

    // Check if job's end date falls within the specified range
    const jobEndDate = new Date(job.date_end);
    return jobEndDate > rangeStart;
  });
}
